import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  Target,
  Users,
  Database,
  Brain,
  Send,
  CheckCircle,
  Pause,
  Play,
} from "lucide-react";

export function Hero() {
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (paused || reducedMotion) video.current?.pause();
    else void video.current?.play().catch(() => {});
  }, [paused, reducedMotion]);
  return (
    <section
      className={`hero-stage cinematic-hero ${paused ? "motion-paused" : ""}`}
      aria-labelledby="hero-title"
    >
      {!reducedMotion && (
        <video
          ref={video}
          className="hero-background-video"
          src="/uniqreach-background.mp4"
          autoPlay={!paused}
          muted
          loop
          playsInline
          disablePictureInPicture
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      <div className="container hero-grid">
        <div className="hero-copy">
          <span className="hero-kicker">
            <span aria-hidden="true" />
            Sales systems for B2B tech
          </span>
          <h1 id="hero-title" className="hero-title">
            <span className="hero-line">
              <span>Your next customer.</span>
            </span>
            <span className="hero-line hero-line-muted">
              <span>
                A better way
                <br className="hero-title-break" /> to reach them.
              </span>
            </span>
          </h1>
          <p className="hero-description">
            We build AI-powered outbound sales systems that find the right
            buyers, make outreach relevant, and give your team more time to
            close.
          </p>
          <div className="hero-actions">
            <Link to="/book" className="btn btn-primary btn-lg">
              Book a Strategy Call
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <Link to="/how-it-works" className="hero-secondary">
              See how it works
              <ArrowUpRightIcon />
            </Link>
          </div>
          <p className="hero-note">
            Better data. Connected workflows. Human expertise.
          </p>
        </div>
        <SystemArchitecturePreview
          paused={paused}
          onToggle={() => setPaused(!paused)}
        />
      </div>
      <div className="container hero-bottom">
        <span>Built around your sales process.</span>
        <a href="#opportunity-title" className="hero-scroll">
          Explore the system
          <ArrowDown size={16} aria-hidden="true" />
        </a>
      </div>
    </section>
  );
}
function ArrowUpRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path d="M7 17 17 7M7 7h10v10" />
    </svg>
  );
}

function SystemArchitecturePreview({
  paused,
  onToggle,
}: {
  paused: boolean;
  onToggle: () => void;
}) {
  const stages = [
    { label: "Define your market", detail: "ICP & strategy", icon: Target },
    {
      label: "Discover prospects",
      detail: "Companies & decision-makers",
      icon: Users,
    },
    {
      label: "Verify the data",
      detail: "Enrichment & quality",
      icon: Database,
    },
    { label: "Add intelligence", detail: "AI research & context", icon: Brain },
    {
      label: "Start conversations",
      detail: "Relevant outreach & follow-up",
      icon: Send,
    },
    {
      label: "Qualify the opportunity",
      detail: "Response handling & CRM",
      icon: CheckCircle,
    },
  ];
  return (
    <div className={`hero-visual ${paused ? "motion-paused" : ""}`}>
      <div className="architecture-header">
        <div>
          <span className="label">The UniqReach engine</span>
          <p>One connected system.</p>
        </div>
        <span className="architecture-badge">System overview</span>
      </div>
      <div className="architecture-stages">
        {stages.map((stage, index) => (
          <div
            className="architecture-stage"
            key={stage.label}
            style={{ "--stage-delay": `${index * 1.8}s` } as CSSProperties}
          >
            <div className="architecture-stage-top">
              <stage.icon size={21} aria-hidden="true" />
              <span className="architecture-index">0{index + 1}</span>
            </div>
            <h2>{stage.label}</h2>
            <p>{stage.detail}</p>
          </div>
        ))}
      </div>
      <div className="architecture-output">
        <div className="output-icon">
          <CheckCircle size={21} aria-hidden="true" />
        </div>
        <div>
          <span className="label">The outcome we work toward</span>
          <p>More qualified conversations.</p>
        </div>
      </div>
      <div className="architecture-footer">
        <span>
          <span className="system-dot" aria-hidden="true" />
          Refine. Learn. Repeat.
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label={
            paused ? "Play system animation" : "Pause system animation"
          }
          aria-pressed={paused}
        >
          {paused ? (
            <Play size={14} aria-hidden="true" />
          ) : (
            <Pause size={14} aria-hidden="true" />
          )}
          <span>{paused ? "Play" : "Pause"} motion</span>
        </button>
      </div>
    </div>
  );
}

export function HeroMinimal({
  title,
  subtitle,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
}: {
  title: string;
  subtitle?: string;
  ctaText: string;
  ctaHref: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
}) {
  return (
    <section className="page-hero" aria-labelledby="hero-title">
      <div className="container">
        <div className="page-hero-inner">
          <span className="label text-accent">
            UniqReach / Sales systems for B2B tech
          </span>
          <h1 id="hero-title">{title}</h1>
          {subtitle && <p>{subtitle}</p>}
          {ctaText && (
            <div className="page-hero-actions">
              <Link to={ctaHref} className="btn btn-primary btn-lg">
                {ctaText}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              {secondaryCtaText && secondaryCtaHref && (
                <Link
                  to={secondaryCtaHref}
                  className="btn btn-secondary btn-lg"
                >
                  {secondaryCtaText}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
